import * as THREE from 'three';
import {
  applySurfaceMaterialDefaults,
  copySurfaceMaterialProperties,
  createSurfaceMaterial,
  surfaceProgramCacheKey,
  type SurfaceMaterialParams,
} from '../core/SurfaceMaterial';
import { applyBakeShaderHooks } from '../core/SurfaceBakeChunks';
import {
  assignDamageFieldUniforms,
  DAMAGE_FIELD_COMMON_GLSL,
  DAMAGE_FIELD_GLSL,
  DAMAGE_FIELD_VERTEX_GLSL,
  DAMAGE_FIELD_VERTEX_SAMPLE,
  damageFieldProgramParts,
  registerDamageFieldConsumer,
} from '../core/DamageFieldBinding';
import {
  applyDamageDecalAtlasDefine,
  assignDamageDecalUniforms,
  DAMAGE_DECAL_ATLAS_DEFINE,
  DAMAGE_DECAL_ATLAS_GLSL,
  DAMAGE_DECAL_VERTEX_GLSL,
  DAMAGE_DECAL_VERTEX_SAMPLE,
} from '../core/DamageDecalAtlas';
import { SURFACE_MICRO_GLSL } from '../core/SurfaceMicroDetail';
import { SURFACE_NOISE_GLSL } from '../core/SurfaceNoise';
import { SURFACE_TRIPLANAR_GLSL } from '../core/TriplanarProjection';
import type { MaterialAppearanceDescriptor } from '../appearanceDescriptor';
import {
  asFiniteNumber,
  asHexColor,
  commonSurfaceControls,
  type SurfaceControlSpec,
  type SurfaceDefinition,
  type SurfaceFace,
} from '../core/SurfaceDefinition';
import { DESTRUCTION_PROFILES } from '../../services/destructionProfiles';

export type WoodQuality = 'high' | 'medium' | 'low';
export type WoodFace = 'exterior' | 'interior';
export type WoodSurfaceState = 'hero' | 'weathered' | 'wet' | 'splintered';

export interface WoodMaterialSettings {
  /** Meters per repeat. Grain remains aligned with rest-space +X. */
  textureScale: number;
  roughness: number;
  normalStrength: number;
  macroVariation: number;
  grainContrast: number;
  quality: WoodQuality;
  seed: number;
  surfaceState: WoodSurfaceState;
  baseTint: string;
  roughnessMin: number;
  roughnessMax: number;
  wearAmount: number;
  /** Multiplier on annual-ring frequency. Default 1 — identity. */
  ringScale: number;
}

export const DEFAULT_WOOD_SETTINGS: WoodMaterialSettings = {
  textureScale: 0.72,
  roughness: 0.72,
  normalStrength: 0.18,
  macroVariation: 0.34,
  grainContrast: 0.78,
  quality: 'medium',
  seed: 8129,
  surfaceState: 'hero',
  baseTint: '#ffffff',
  roughnessMin: 0.2,
  roughnessMax: 0.96,
  wearAmount: 0,
  ringScale: 1,
};

/** Warm splinters + pale sawdust motes, matched to early/late-wood albedo. */
export const appearanceDescriptor = {
  version: 1,
  dust: { color: '#efe0b8', lingerScale: 0.85, settleSpeed: 0.16 },
  chips: { color: '#c46a28' },
  debrisTint: '#8e4213',
  particleStyle: { splinters: true },
  fragment: { outer: '#8b5a2b', interior: '#d4c4a8' },
} as const satisfies MaterialAppearanceDescriptor;

interface WoodUniforms {
  uWoodTextureScale: { value: number };
  uWoodRoughness: { value: number };
  uWoodNormalStrength: { value: number };
  uWoodMacroVariation: { value: number };
  uWoodGrainContrast: { value: number };
  uWoodQuality: { value: number };
  uWoodSeed: { value: number };
  uWoodInteriorMode: { value: number };
  uWoodSurfaceState: { value: number };
  uWoodBaseTint: { value: THREE.Color };
  uWoodRoughnessMin: { value: number };
  uWoodRoughnessMax: { value: number };
  uWoodWearAmount: { value: number };
  uWoodRingScale: { value: number };
}
interface WoodMaterialState { face: WoodFace; settings: WoodMaterialSettings; uniforms: WoodUniforms; }
const woodStates = new WeakMap<THREE.Material, WoodMaterialState>();

const WOOD_GLSL = /* glsl */ `
uniform float uWoodTextureScale;
uniform float uWoodRoughness;
uniform float uWoodNormalStrength;
uniform float uWoodMacroVariation;
uniform float uWoodGrainContrast;
uniform float uWoodQuality;
uniform float uWoodSeed;
uniform float uWoodInteriorMode;
uniform float uWoodSurfaceState;
uniform vec3 uWoodBaseTint;
uniform float uWoodRoughnessMin;
uniform float uWoodRoughnessMax;
uniform float uWoodWearAmount;
uniform float uWoodRingScale;

float woodFbm(vec3 p, float wavelengthMeters, float fp) {
  float v = surfaceNoiseBL(p, uWoodSeed * 17.13, wavelengthMeters, fp);
  if (uWoodQuality > 0.5) v += surfaceNoiseBL(p * 2.01 + vec3(13,29,41), uWoodSeed * 17.13, wavelengthMeters / 2.01, fp) * 0.45;
  if (uWoodQuality > 1.5) v += surfaceNoiseBL(p * 4.07 + vec3(31,7,19), uWoodSeed * 17.13, wavelengthMeters / 4.07, fp) * 0.2;
  return v / (uWoodQuality > 1.5 ? 1.65 : (uWoodQuality > 0.5 ? 1.45 : 1.0));
}

void woodSurfaceFields(vec3 restPos, vec3 restNormal, out float broad, out float grain, out float knot, out float height) {
  float scale = max(uWoodTextureScale, 0.05);
  float fp = surfacePixelFootprint(restPos);
  // Grain runs along rest-space +X, so annual-ring variation lives in the
  // transverse YZ plane. This keeps the front wall's grain horizontal instead
  // of turning it into vertical bands.
  vec3 p = restPos / scale;
  vec2 transverse = p.yz;
  // ringFreq 7.5 → ~9.6 cm rings at default scale 0.72 (was ~0.76 m, a smooth gradient).
  float ringFreq = 7.5 * max(uWoodRingScale, 0.05);
  float ringWl = scale / max(ringFreq * 0.95, 0.01);
  float ringFade = surfaceOctaveFade(ringWl, fp);
  float ringWarp = woodFbm(vec3(p.x * 0.16, transverse * 1.25) + vec3(5.0, 13.0, 7.0), scale / 1.25, fp);
  float ringPhase = (transverse.x * 0.95 + transverse.y * 0.22 + ringWarp * 1.15) * ringFreq;
  float ringT = fract(ringPhase);
  float dRing = min(ringT, 1.0 - ringT);
  float ringId = floor(ringPhase);
  float lateW = mix(0.12, 0.34, surfaceHash_(vec3(ringId, 2.0, 8.0), uWoodSeed * 17.13));
  float lateHalf = max(0.06, (fp / max(ringWl, 1e-5)) * 0.55);
  float latewood = (1.0 - smoothstep(lateW - lateHalf, lateW + lateHalf, dRing)) * ringFade;
  float poreNoise = woodFbm(vec3(p.x * 0.15, transverse * 5.5) + vec3(17.0, 29.0, 41.0), scale / 5.5, fp);
  float fiberWl = scale / 22.0;
  float fiberFade = surfaceOctaveFade(fiberWl, fp);
  float fiber = woodFbm(vec3(p.x * 22.0, transverse * 0.85) + vec3(3.0, 9.0, 2.0), fiberWl, fp);
  grain = clamp(latewood * 0.70 + poreNoise * 0.24 + (fiber - 0.5) * 0.14 * fiberFade, 0.0, 1.0);
  grain = mix(0.5, grain, ringFade);
  broad = woodFbm(vec3(p.x * 0.08, transverse * 0.75) + vec3(19,3,31), scale / 0.75, fp);
  knot = uWoodQuality > 0.5 ? smoothstep(0.70, 0.92, woodFbm(vec3(p.x * 0.05, transverse * 1.35) + vec3(29,11,2), scale / 1.35, fp)) : 0.0;
  height = 0.5 + (grain - 0.5) * 0.022 * ringFade + (broad - 0.5) * 0.03 - knot * 0.06;
  if (uWoodInteriorMode > 0.5) {
    height = 0.5 + (broad - 0.5) * 0.06 - knot * 0.04;
  }
}
`;

const WOOD_VERTEX_INJECTION = /* glsl */ `
attribute vec3 surfaceRestPosition;
varying vec3 vWoodRestPosition;
varying vec3 vWoodRestNormal;
${DAMAGE_FIELD_COMMON_GLSL}
${DAMAGE_FIELD_VERTEX_GLSL}
${DAMAGE_DECAL_VERTEX_GLSL}
`;
const WOOD_FRAGMENT_INJECTION = /* glsl */ `
uniform mat3 normalMatrix;
varying vec3 vWoodRestPosition;
varying vec3 vWoodRestNormal;
${SURFACE_TRIPLANAR_GLSL}
${SURFACE_NOISE_GLSL}
${SURFACE_MICRO_GLSL}
${DAMAGE_FIELD_GLSL}
${DAMAGE_DECAL_ATLAS_GLSL}
${WOOD_GLSL}
float gWoodBroad;
float gWoodGrain;
float gWoodKnot;
float gWoodHeight;
`;

function clamp01(value: number, fallback = 0): number { return Math.min(Math.max(Number.isFinite(value) ? value : fallback, 0), 1); }
function qualityValue(value: WoodQuality): number { return value === 'high' ? 2 : value === 'medium' ? 1 : 0; }
function stateValue(value: WoodSurfaceState): number { return value === 'weathered' ? 1 : value === 'wet' ? 2 : value === 'splintered' ? 3 : 0; }
function normalizeSettings(settings: WoodMaterialSettings): WoodMaterialSettings {
  return {
    textureScale: Number.isFinite(settings.textureScale) && settings.textureScale > 0 ? settings.textureScale : DEFAULT_WOOD_SETTINGS.textureScale,
    roughness: clamp01(settings.roughness, DEFAULT_WOOD_SETTINGS.roughness),
    normalStrength: Math.min(Math.max(Number.isFinite(settings.normalStrength) ? settings.normalStrength : 0, 0), 0.8),
    macroVariation: clamp01(settings.macroVariation, DEFAULT_WOOD_SETTINGS.macroVariation),
    grainContrast: clamp01(settings.grainContrast, DEFAULT_WOOD_SETTINGS.grainContrast),
    quality: settings.quality === 'high' || settings.quality === 'low' ? settings.quality : 'medium',
    seed: Number.isFinite(settings.seed) ? settings.seed : DEFAULT_WOOD_SETTINGS.seed,
    surfaceState: settings.surfaceState === 'weathered' || settings.surfaceState === 'wet' || settings.surfaceState === 'splintered' ? settings.surfaceState : 'hero',
    baseTint: asHexColor(settings.baseTint, DEFAULT_WOOD_SETTINGS.baseTint),
    roughnessMin: clamp01(asFiniteNumber(settings.roughnessMin, DEFAULT_WOOD_SETTINGS.roughnessMin), DEFAULT_WOOD_SETTINGS.roughnessMin),
    roughnessMax: clamp01(asFiniteNumber(settings.roughnessMax, DEFAULT_WOOD_SETTINGS.roughnessMax), DEFAULT_WOOD_SETTINGS.roughnessMax),
    wearAmount: clamp01(asFiniteNumber(settings.wearAmount, DEFAULT_WOOD_SETTINGS.wearAmount), DEFAULT_WOOD_SETTINGS.wearAmount),
    ringScale: Number.isFinite(settings.ringScale) && settings.ringScale > 0 ? settings.ringScale : DEFAULT_WOOD_SETTINGS.ringScale,
  };
}
function createUniforms(settings: WoodMaterialSettings, face: WoodFace): WoodUniforms {
  return {
    uWoodTextureScale: { value: settings.textureScale }, uWoodRoughness: { value: settings.roughness },
    uWoodNormalStrength: { value: settings.normalStrength }, uWoodMacroVariation: { value: settings.macroVariation },
    uWoodGrainContrast: { value: settings.grainContrast }, uWoodQuality: { value: qualityValue(settings.quality) },
    uWoodSeed: { value: settings.seed }, uWoodInteriorMode: { value: face === 'interior' ? 1 : 0 },
    uWoodSurfaceState: { value: stateValue(settings.surfaceState) },
    uWoodBaseTint: { value: new THREE.Color(settings.baseTint) },
    uWoodRoughnessMin: { value: Math.min(settings.roughnessMin, settings.roughnessMax) },
    uWoodRoughnessMax: { value: Math.max(settings.roughnessMin, settings.roughnessMax) },
    uWoodWearAmount: { value: settings.wearAmount },
    uWoodRingScale: { value: settings.ringScale },
  };
}
function syncUniforms(state: WoodMaterialState): void {
  const { settings, uniforms, face } = state;
  uniforms.uWoodTextureScale.value = settings.textureScale; uniforms.uWoodRoughness.value = settings.roughness;
  uniforms.uWoodNormalStrength.value = settings.normalStrength; uniforms.uWoodMacroVariation.value = settings.macroVariation;
  uniforms.uWoodGrainContrast.value = settings.grainContrast; uniforms.uWoodQuality.value = qualityValue(settings.quality);
  uniforms.uWoodSeed.value = settings.seed; uniforms.uWoodInteriorMode.value = face === 'interior' ? 1 : 0;
  uniforms.uWoodSurfaceState.value = stateValue(settings.surfaceState);
  uniforms.uWoodBaseTint.value.set(settings.baseTint);
  uniforms.uWoodRoughnessMin.value = Math.min(settings.roughnessMin, settings.roughnessMax);
  uniforms.uWoodRoughnessMax.value = Math.max(settings.roughnessMin, settings.roughnessMax);
  uniforms.uWoodWearAmount.value = settings.wearAmount;
  uniforms.uWoodRingScale.value = settings.ringScale;
}

function compileWoodShader(shader: { uniforms: Record<string, { value: unknown }>; vertexShader: string; fragmentShader: string }, state: WoodMaterialState): void {
  Object.assign(shader.uniforms, state.uniforms);
  assignDamageFieldUniforms(shader);
  assignDamageDecalUniforms(shader);
  shader.vertexShader = shader.vertexShader.replace('#include <common>', `${WOOD_VERTEX_INJECTION}\n#include <common>`).replace('#include <worldpos_vertex>', `#include <worldpos_vertex>\nvWoodRestPosition = surfaceRestPosition;\nvWoodRestNormal = normalize(normal);\n${DAMAGE_FIELD_VERTEX_SAMPLE}\n${DAMAGE_DECAL_VERTEX_SAMPLE}`);
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `${WOOD_FRAGMENT_INJECTION}\n#include <common>`)
    .replace('#include <color_fragment>', `#include <color_fragment>
gSurfaceSpecAaRough = 0.0;
woodSurfaceFields(vWoodRestPosition, vWoodRestNormal, gWoodBroad, gWoodGrain, gWoodKnot, gWoodHeight);
float woodMicro = surfaceMicroField(vWoodRestPosition, max(uWoodTextureScale * 0.08, 0.01), uWoodSeed + 43.0);
float woodLodFade = surfaceMicroFade(vWoodRestPosition, max(uWoodTextureScale * 0.08, 0.01));
float woodPoreScale = max(uWoodTextureScale, 0.05);
float woodPoreFade = surfaceOctaveFade(woodPoreScale / 8.0, surfacePixelFootprint(vWoodRestPosition));
float woodPore = smoothstep(0.62, 0.9, woodFbm(vec3(vWoodRestPosition.x / woodPoreScale * 0.18, vWoodRestPosition.yz / woodPoreScale * 8.0) + vec3(43,7,19), woodPoreScale / 8.0, surfacePixelFootprint(vWoodRestPosition))) * woodPoreFade;
float grainBand = mix(0.12, clamp(gWoodGrain, 0.0, 1.0), 0.50 + 0.50 * uWoodGrainContrast);
vec3 springWood = vec3(0.62, 0.36, 0.14);
vec3 lateWood = vec3(0.20, 0.07, 0.022);
vec3 woodAlbedo = mix(springWood, lateWood, grainBand);
woodAlbedo *= 0.86 + gWoodBroad * 0.22 + (woodMicro - 0.5) * 0.1;
woodAlbedo = mix(woodAlbedo, woodAlbedo * 0.72, woodPore * 0.28);
woodAlbedo = mix(woodAlbedo, vec3(0.2, 0.07, 0.025), gWoodKnot * 0.38);
if (uWoodInteriorMode > 0.5) {
  // Torn end-grain. Tonal anchor: appearance.fragment.interior #d4c4a8.
  // Sharp elongated splinter streaks — previous mix was ~4× too faint.
  vec3 woodInteriorTone = vec3(0.831, 0.769, 0.659);
  vec3 woodN = normalize(vWoodRestNormal);
  vec3 woodW = surfaceTriplanarWeights(woodN);
  vec2 woodUv = (vWoodRestPosition.yz * woodW.x + vWoodRestPosition.xz * woodW.y + vWoodRestPosition.xy * woodW.z);
  float woodFp = surfacePixelFootprint(vWoodRestPosition);
  float woodM = max(uWoodTextureScale, 0.05);
  float woodBand = woodFbm(vec3(woodUv.x * 3.4, woodUv.y * 0.55, 0.0) + vec3(5.0, 13.0, 7.0), woodM / 3.4, woodFp);
  float woodTear = woodFbm(vec3(woodUv.x * 0.85, woodUv.y * 16.0, 0.0) + vec3(17.0, 3.0, 29.0), woodM / 16.0, woodFp);
  float woodTearFade = surfaceOctaveFade(woodM / 16.0, woodFp);
  float woodTearAmt = smoothstep(0.50, 0.62, woodTear) * woodTearFade;
  float woodSplinter = woodFbm(vec3(woodUv.x * 0.55, woodUv.y * 28.0, 0.0) + vec3(8.0, 21.0, 4.0), woodM / 28.0, woodFp);
  float woodSplinterFade = surfaceOctaveFade(woodM / 28.0, woodFp);
  float woodSplinterAmt = smoothstep(0.66, 0.78, woodSplinter) * woodSplinterFade;
  float woodChip = woodFbm(vec3(woodUv * 4.8, 0.0) + vec3(9.0, 21.0, 4.0), woodM / 4.8, woodFp);
  woodAlbedo = mix(woodInteriorTone * 0.58, woodInteriorTone * 1.22, woodBand);
  woodAlbedo = mix(woodAlbedo, woodAlbedo * vec3(0.28, 0.14, 0.06), woodTearAmt);
  woodAlbedo = mix(woodAlbedo, woodAlbedo * vec3(0.18, 0.09, 0.04), woodSplinterAmt * 0.90);
  woodAlbedo = mix(woodAlbedo, woodAlbedo * vec3(0.48, 0.32, 0.16), smoothstep(0.58, 0.78, woodChip) * 0.50);
}
if (uWoodSurfaceState > 0.5 && uWoodSurfaceState < 1.5) woodAlbedo *= mix(1.0, 0.75, gWoodBroad * 0.5);
else if (uWoodSurfaceState > 1.5 && uWoodSurfaceState < 2.5) woodAlbedo *= 0.72 + gWoodGrain * 0.2;
else if (uWoodSurfaceState > 2.5) woodAlbedo = mix(woodAlbedo, vec3(0.78, 0.57, 0.34), gWoodKnot * 0.48);
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vWoodRestPosition, normalize(vWoodRestNormal));
float woodFresh = 1.0 - gDamageAge * 0.32;
vec3 woodDamageInterior = vec3(0.831, 0.769, 0.659);
#ifdef USE_DAMAGE_DECAL_ATLAS
sampleWoodDamageDecals(vWoodRestPosition, normalize(vWoodRestNormal));
woodAlbedo = mix(woodAlbedo, woodAlbedo * vec3(0.92, 0.88, 0.82), gDamageBlotch * 0.04 * woodFresh);
float woodDmgFp = surfacePixelFootprint(vWoodRestPosition);
float woodDmgM = max(uWoodTextureScale, 0.05);
vec3 woodDmgP = vWoodRestPosition * (1.0 / woodDmgM);
float gWoodCraterGrain = surfaceNoiseBL(vec3(woodDmgP.x * 3.2, woodDmgP.y * 18.0, woodDmgP.z * 18.0) + vec3(5.0, 2.0, 9.0), uWoodSeed * 17.13, woodDmgM / 18.0, woodDmgFp) * 0.48
  + surfaceNoiseBL(vec3(woodDmgP.x * 6.5, woodDmgP.y * 32.0, woodDmgP.z * 32.0) + vec3(11.0, 4.0, 3.0), uWoodSeed * 17.13, woodDmgM / 32.0, woodDmgFp) * 0.34
  + surfaceNoiseBL(vec3(woodDmgP.x * 1.6, woodDmgP.y * 8.0, woodDmgP.z * 8.0) + vec3(2.0, 13.0, 7.0), uWoodSeed * 17.13, woodDmgM / 8.0, woodDmgFp) * 0.18
  + gWoodGrain * 0.12;
vec3 woodSpallMean = mix(woodDamageInterior * 1.04, woodDamageInterior * 0.78, clamp(gDecalDepth, 0.0, 1.0));
woodSpallMean *= mix(0.80, 1.24, gWoodCraterGrain);
woodSpallMean = mix(woodSpallMean, woodSpallMean * vec3(0.78, 0.62, 0.42), gDecalCrack * 0.28);
float woodSpallAmt = gDecalSpall * mix(0.92, 1.0, clamp(gDecalDepth, 0.0, 1.0));
woodAlbedo = mix(woodAlbedo, woodSpallMean, woodSpallAmt);
woodAlbedo = mix(woodAlbedo, woodAlbedo * vec3(0.70, 0.55, 0.38), gWoodFurrow * gDecalDepth * 0.40);
vec3 woodRimTint = mix(woodDamageInterior * 1.22, woodAlbedo * vec3(1.20, 1.14, 1.04), 0.3);
woodAlbedo = mix(woodAlbedo, woodRimTint, gDecalRim * 0.95 * gDecalFresh);
vec3 woodCrackTint = woodAlbedo * mix(vec3(0.70, 0.58, 0.42), vec3(0.48, 0.36, 0.24), gDecalCrackCore);
woodAlbedo = mix(woodAlbedo, min(woodAlbedo, woodCrackTint), gDecalCrack * 0.88);
#else
float woodFiber = abs(fract(vWoodRestPosition.y * 14.0 + vWoodRestPosition.z * 2.2 + gWoodGrain * 0.45) - 0.5);
float woodTear = (1.0 - smoothstep(0.0, mix(0.012, 0.04, gDamageBlotch), woodFiber)) * gDamageBlotch;
woodAlbedo = mix(woodAlbedo, woodDamageInterior, gDamageBlotch * mix(0.30, 0.64, clamp(gDamage, 0.0, 1.0)) * woodFresh);
woodAlbedo = mix(woodAlbedo, vec3(0.16, 0.055, 0.018), woodTear * 0.36 * woodFresh);
woodAlbedo = mix(woodAlbedo, woodAlbedo * vec3(0.16, 0.08, 0.04), gDamageCore * 0.78 * woodFresh);
woodAlbedo = mix(woodAlbedo, woodAlbedo * 0.28, gDamageCrack * woodFresh);
#endif
#endif
woodAlbedo *= uWoodBaseTint;
woodAlbedo = mix(woodAlbedo, woodAlbedo * vec3(0.78, 0.72, 0.62), clamp(uWoodWearAmount, 0.0, 1.0));
diffuseColor.rgb = woodAlbedo;`)
    .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
roughnessFactor = clamp(uWoodRoughness + (0.5 - gWoodGrain) * 0.14 + gWoodKnot * 0.14 + (woodMicro - 0.5) * 0.08, 0.2, 0.94);
roughnessFactor = clamp(roughnessFactor + woodPore * 0.06, 0.2, 0.96);
roughnessFactor = clamp(roughnessFactor + (1.0 - woodLodFade) * 0.05, 0.2, 0.96);
// Sink Toksvig slot (filled after surfaceHeightRestNormal in normal_fragment_maps; reapplied there).
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.2, 0.96);
if (uWoodSurfaceState > 1.5 && uWoodSurfaceState < 2.5) roughnessFactor = clamp(roughnessFactor - gWoodGrain * 0.16, 0.12, 0.94);
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
roughnessFactor = clamp(
  roughnessFactor
    + gDamageBlotch * 0.03
    + gDecalSpall * 0.12
    + gDecalDepth * 0.10
    + gDecalCrack * 0.08
    + gWoodFurrow * 0.06,
  0.12,
  0.96
);
#else
roughnessFactor = clamp(roughnessFactor + gDamageBlotch * 0.08 + gDamageCrack * 0.08, 0.12, 0.96);
#endif
#endif`)
    .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
vec3 woodBaseN = normalize(vWoodRestNormal);
vec3 woodRelief = surfaceHeightRestNormal(woodBaseN, vWoodRestPosition, gWoodHeight, uWoodNormalStrength * 0.72 * surfaceOctaveFade(max(uWoodTextureScale, 0.05) / 5.5, surfacePixelFootprint(vWoodRestPosition)));
vec3 woodMicroN = surfaceMicroNormal(woodBaseN, vWoodRestPosition, max(uWoodTextureScale * 0.08, 0.01), uWoodNormalStrength * 0.24, uWoodSeed + 43.0);
normal = normalize(normal + normalMatrix * ((woodRelief - woodBaseN) + (woodMicroN - woodBaseN)));
// Apply sink Toksvig after height normals fill gSurfaceSpecAaRough (three.js runs this after roughnessmap_fragment).
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.12, 0.96);
if (uWoodInteriorMode > 0.5) {
  roughnessFactor = clamp(roughnessFactor + 0.08, 0.28, 0.98);
}
float woodRSpan = max(0.76, 1e-4);
float woodRT = clamp((roughnessFactor - 0.20) / woodRSpan, 0.0, 1.0);
roughnessFactor = mix(uWoodRoughnessMin, max(uWoodRoughnessMin, uWoodRoughnessMax), woodRT);`)
    .replace('#include <aomap_fragment>', `#include <aomap_fragment>
reflectedLight.indirectDiffuse *= 1.0 - gWoodKnot * 0.12;
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
reflectedLight.indirectDiffuse *= 1.0 - gDecalAo * 0.42 - gDecalDepth * 0.10;
#endif
#endif`);
}
function applyWoodDefaults(material: THREE.MeshStandardMaterial, settings: WoodMaterialSettings): void {
  applySurfaceMaterialDefaults(material, { roughness: settings.roughness, metalness: 0, shaderOwnsAlbedo: true });
}
export function createWoodMaterial(params: SurfaceMaterialParams, settings: WoodMaterialSettings, face: WoodFace): THREE.MeshStandardMaterial {
  const normalized = normalizeSettings(settings);
  const material = createSurfaceMaterial(params, { roughness: normalized.roughness, metalness: 0, shaderOwnsAlbedo: true });
  const state: WoodMaterialState = { face, settings: normalized, uniforms: createUniforms(normalized, face) };
  applyWoodDefaults(material, normalized);
  material.onBeforeCompile = (shader) => {
    compileWoodShader(shader, state);
    applyBakeShaderHooks(shader, material);
  };
  const decalEnabled = face === 'exterior';
  if (decalEnabled) applyDamageDecalAtlasDefine(material);
  material.customProgramCacheKey = () =>
    surfaceProgramCacheKey(
      'wood',
      5,
      state.face,
      state.settings.quality,
      decalEnabled ? DAMAGE_DECAL_ATLAS_DEFINE : 'no-decal',
      ...damageFieldProgramParts(),
    );
  woodStates.set(material, state);
  registerDamageFieldConsumer(material);
  return material;
}
export function isWoodMaterial(material: THREE.Material): material is THREE.MeshStandardMaterial {
  return material instanceof THREE.MeshStandardMaterial && woodStates.has(material);
}
export function updateWoodMaterial(material: THREE.Material, settings: WoodMaterialSettings): void {
  const state = woodStates.get(material);
  if (!state) throw new TypeError('updateWoodMaterial expects a material created by createWoodMaterial');
  state.settings = normalizeSettings(settings); syncUniforms(state);
  if (material instanceof THREE.MeshStandardMaterial) { applyWoodDefaults(material, state.settings); material.needsUpdate = true; }
}
export function cloneWoodMaterial(source: THREE.Material): THREE.MeshStandardMaterial {
  const state = woodStates.get(source);
  if (!state || !(source instanceof THREE.MeshStandardMaterial)) throw new TypeError('cloneWoodMaterial expects a material created by createWoodMaterial');
  const clone = createWoodMaterial({ color: source.color.clone(), roughness: source.roughness, metalness: source.metalness, flatShading: source.flatShading, side: source.side, transparent: source.transparent, opacity: source.opacity, depthWrite: source.depthWrite, alphaTest: source.alphaTest, vertexColors: source.vertexColors, name: source.name }, state.settings, state.face);
  copySurfaceMaterialProperties(source, clone);
  return clone;
}

const WOOD_CONTROLS: SurfaceControlSpec[] = [
  ...commonSurfaceControls(DEFAULT_WOOD_SETTINGS),
  {
    key: 'grainContrast',
    label: 'Grain contrast',
    kind: 'range',
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_WOOD_SETTINGS.grainContrast,
    group: 'detail',
  },
  {
    key: 'ringScale',
    label: 'Ring scale',
    kind: 'range',
    min: 0.25,
    max: 2.5,
    step: 0.01,
    default: DEFAULT_WOOD_SETTINGS.ringScale,
    group: 'detail',
  },
];

export const SURFACE_DEFINITION: SurfaceDefinition<WoodMaterialSettings> = {
  id: 'wood',
  label: 'Wood',
  appearance: appearanceDescriptor,
  physics: DESTRUCTION_PROFILES.wood,
  defaultSettings: DEFAULT_WOOD_SETTINGS,
  controls: WOOD_CONTROLS,
  settingsFromControls(values) {
    return {
      ...DEFAULT_WOOD_SETTINGS,
      seed: asFiniteNumber(values.seed, DEFAULT_WOOD_SETTINGS.seed),
      textureScale: asFiniteNumber(values.textureScale, DEFAULT_WOOD_SETTINGS.textureScale),
      baseTint: asHexColor(values.baseTint, DEFAULT_WOOD_SETTINGS.baseTint),
      roughnessMin: asFiniteNumber(values.roughnessMin, DEFAULT_WOOD_SETTINGS.roughnessMin),
      roughnessMax: asFiniteNumber(values.roughnessMax, DEFAULT_WOOD_SETTINGS.roughnessMax),
      normalStrength: asFiniteNumber(values.normalStrength, DEFAULT_WOOD_SETTINGS.normalStrength),
      wearAmount: asFiniteNumber(values.wearAmount, DEFAULT_WOOD_SETTINGS.wearAmount),
      grainContrast: asFiniteNumber(values.grainContrast, DEFAULT_WOOD_SETTINGS.grainContrast),
      ringScale: asFiniteNumber(values.ringScale, DEFAULT_WOOD_SETTINGS.ringScale),
    };
  },
  createMaterial(params, settings, face: SurfaceFace) {
    return createWoodMaterial(params, settings, face);
  },
  updateMaterial: updateWoodMaterial,
  cloneMaterial: cloneWoodMaterial,
  isMaterial: isWoodMaterial,
};
